// @ts-nocheck
import React, { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';

import {
  DashboardHeader,
  CriticalActionsSection,
  ShipmentsWithoutBookingsSection,
  UpcomingShipmentsSection,
  DocumentCutoffsSection,
  ShipmentsEnRouteSection,
  ArrivedWithoutInvoiceSection,
  DataConfirmationSection,
} from '../components/dashboard';

// Types
import type {
  CriticalTask,
  ShipmentWithoutBooking,
  UpcomingShipment,
  DocumentCutoff,
  ArrivingShipment,
  ArrivedShipment,
} from '../components/dashboard';

// Design System
import LoadingState from '../components/design-system/LoadingState';
import { ErrorDisplay } from '../components/design-system/ErrorBoundary';

// Services & Context
import { dashboardApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { AriaHelper } from '../utils/accessibility';

// Styles
import './Dashboard.css';

// Vessel tracking type for vessels en route
interface VesselTracking {
  vesselVoy: string;
  containers: number;
  etd: string;
  eta: string;
  voyageTime: number;
  elapsed: number;
  progress: number;
  destination: string;
  shipmentCount: number;
  portcastBookmarkId?: string | null;
}

interface DashboardData {
  criticalTasks: CriticalTask[];
  shipmentsWithoutBookings: ShipmentWithoutBooking[];
  upcomingShipments: UpcomingShipment[];
  documentCutoffs: DocumentCutoff[];
  arrivingShipments: ArrivingShipment[];
  vesselsEnRoute: VesselTracking[];
  arrivedWithoutInvoice: ArrivedShipment[];
  stats: {
    criticalTasks: number;
    urgentCutoffs: number;
    arrivingSoon: number;
    shipmentsEnRoute: number;
    shipmentsWithoutBookingsCounts?: {
      past: number;
      thirty: number;
      sixty: number;
      ninety: number;
      discretionary: number;
    };
    upcomingShipmentsCounts?: {
      thirty: number;
      sixty: number;
      ninety: number;
    };
  };
}

// Transform API data to component format
const transformDashboardData = (apiData: any): DashboardData => {
  // Critical Tasks - already in correct format from API
  const criticalTasks: CriticalTask[] = (apiData?.criticalTasks || []).map((task: any) => ({
    id: task.id,
    reference: task.reference,
    taskType: task.taskType,
    priority: task.priority,
    supplier: task.supplier,
    buyer: task.buyer,
    dueDate: task.dueDate || '',
    timeRemaining: task.timeRemaining || 'Soon',
    isOverdue: task.isOverdue || false,
  }));

  // Shipments Without Bookings - now uses 'category' from API (legacy naming)
  const shipmentsWithoutBookings: ShipmentWithoutBooking[] = (apiData?.shipmentsWithoutBookings || []).map((s: any) => ({
    id: s.id,
    reference: s.reference,
    contractRef: s.contractRef,
    supplier: s.supplier,
    supplierCode: s.supplierCode,
    buyer: s.buyer,
    buyerCode: s.buyerCode,
    buyerName: s.buyerName,
    pol: s.pol,
    pod: s.pod,
    start: s.start,
    end: s.end,
    shipmentDisplay: s.shipmentDisplay,
    containerCount: s.containerCount,
    daysUntilStart: s.daysUntilStart,
    // Map 'category' to 'period' for component compatibility
    period: s.category,
    category: s.category,
    isOverdue: s.category === 'past',
  }));

  // Upcoming Shipments - now uses 'category' from API
  const upcomingShipments: UpcomingShipment[] = (apiData?.upcomingShipments || []).map((s: any) => ({
    id: s.id,
    reference: s.reference,
    contractRef: s.contractRef,
    supplier: s.supplier,
    supplierCode: s.supplierCode,
    buyer: s.buyer,
    buyerCode: s.buyerCode,
    cyCut: s.cyCut,
    eta: s.eta,
    siSent: s.siSent,
    siGmailId: s.siGmailId,
    category: s.category,
    daysUntilCyCut: s.daysUntilCyCut,
  }));

  // Document Cutoffs
  const documentCutoffs: DocumentCutoff[] = (apiData?.documentCutoffs || []).map((d: any) => ({
    id: d.id,
    shipmentId: d.shipmentId,
    reference: d.reference,
    contractRef: d.contractRef,
    supplier: d.supplier,
    supplierCode: d.supplierCode,
    buyer: d.buyer,
    buyerCode: d.buyerCode,
    cutoffDate: d.cutoffDate,
    cutoffDateTime: d.cutoffDateTime,
    hoursRemaining: d.hoursRemaining,
    isUrgent: d.isUrgent,
  }));

  const arrivingShipments: ArrivingShipment[] = (apiData?.arrivingShipments || []).map((s: any) => ({
    id: s.id,
    reference: s.reference,
    supplier: s.supplier,
    supplierCode: s.supplierCode,
    buyer: s.buyer,
    buyerCode: s.buyerCode,
    eta: s.eta,
    etaRaw: s.etaRaw,
    trackingStatus: s.trackingStatus || 'N_A',
    containerStatus: s.containerStatus || 'N/A',
    containerStatusDate: s.containerStatusDate || null,
    containerStatusTimezone: s.containerStatusTimezone || null,
  }));

  // Vessels En Route - new grouped format with shipment details
  const vesselsEnRoute: VesselTracking[] = (apiData?.vesselsEnRoute || []).map((v: any) => ({
    vesselVoy: v.vesselVoy,
    containers: v.containers,
    etd: v.etd,
    eta: v.eta,
    voyageTime: v.voyageTime,
    elapsed: v.elapsed,
    progress: v.progress,
    destination: v.destination,
    shipmentCount: v.shipmentCount,
    portcastBookmarkId: v.portcastBookmarkId,
    carrier: v.carrier,
    carrierCode: v.carrierCode,
    shipments: v.shipments,
  }));

  const arrivedWithoutInvoice: ArrivedShipment[] = (apiData?.arrivedWithoutInvoice || []).map((s: any) => ({
    id: s.id,
    reference: s.reference,
    supplier: s.supplier,
    supplierCode: s.supplierCode,
    eta: s.eta,
    arrivalDate: s.eta,
  }));

  const stats = apiData?.stats || {
    criticalTasks: criticalTasks.length,
    urgentCutoffs: documentCutoffs.length,
    arrivingSoon: arrivingShipments.length,
    shipmentsEnRoute: vesselsEnRoute.reduce((sum: number, v: any) => sum + (v.shipments?.length || v.shipmentCount || 0), 0),
  };

  return {
    criticalTasks,
    shipmentsWithoutBookings,
    upcomingShipments,
    documentCutoffs,
    arrivingShipments,
    vesselsEnRoute,
    arrivedWithoutInvoice,
    stats,
  };
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const dashboardRef = useRef<HTMLDivElement>(null);

  // Fetch dashboard data
  const { data: rawData, isLoading, error } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: async () => {
      const response = await dashboardApi.getSummary();
      return response;
    },
    refetchInterval: 60000, // Refresh every minute
    retry: 1,
  });

  // Transform data for components
  const data = rawData ? transformDashboardData(rawData) : null;

  // Announce dashboard updates to screen readers
  useEffect(() => {
    if (data?.stats) {
      const totalTasks = data.stats.criticalTasks;
      if (totalTasks > 0) {
        AriaHelper.announce(
          `Dashboard loaded. You have ${totalTasks} critical tasks.`,
          'polite'
        );
      }
    }
  }, [data?.stats]);

  // Handler functions
  const handleSendRequest = (task: CriticalTask) => {
    message.info(`Opening email for ${task.reference}...`);
    navigate(`/contracts/${task.reference}`);
  };

  const handleSearchGmail = (item: any) => {
    const searchQuery = encodeURIComponent(item.reference);
    window.open(`https://mail.google.com/mail/u/0/#search/${searchQuery}`, '_blank');
  };

  const handleSendBR = (shipment: ShipmentWithoutBooking) => {
    message.info(`Opening booking request for ${shipment.reference}...`);
    navigate(`/contracts/${shipment.reference}`);
  };

  const handleSendSI = (shipment: UpcomingShipment) => {
    message.info(`Opening shipping instruction for ${shipment.reference}...`);
    navigate(`/contracts/${shipment.reference}`);
  };

  const handleViewEmail = (shipment: UpcomingShipment) => {
    const searchQuery = encodeURIComponent(shipment.reference);
    window.open(`https://mail.google.com/mail/u/0/#search/${searchQuery}`, '_blank');
  };

  const handleCreateInvoice = (shipment: ArrivedShipment) => {
    message.info(`Opening invoice creation for ${shipment.reference}...`);
    navigate(`/contracts/${shipment.reference}?tab=payments`);
  };

  const handleStatClick = (statType: string) => {
    // Scroll to relevant section
    const sectionMap: Record<string, string> = {
      criticalTasks: 'critical-actions',
      urgentCutoffs: 'document-cutoffs',
      arrivingSoon: 'shipments-arriving',
      shipmentsEnRoute: 'shipments-en-route',
    };
    const sectionId = sectionMap[statType];
    if (sectionId) {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="dashboard">
        <LoadingState 
          resource="dashboard data"
          variant="skeleton-card"
          style={{ minHeight: '400px' }}
        />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard">
        <ErrorDisplay 
          error={error as Error}
          title="Unable to Load Dashboard"
          showRetry
          onRetry={() => window.location.reload()}
          style={{ minHeight: '400px' }}
        />
      </div>
    );
  }

  return (
    <div 
      ref={dashboardRef}
      className="dashboard"
      role="main"
      aria-label="Broker Dashboard"
    >
      {/* Header with Quick Stats */}
      <DashboardHeader
        userName={user?.firstName || 'User'}
        stats={data?.stats || { criticalTasks: 0, urgentCutoffs: 0, arrivingSoon: 0, shipmentsEnRoute: 0 }}
        onStatClick={handleStatClick}
      />

      {/* Data Confirmation Banner - shows when pending updates exist */}
      <DataConfirmationSection />

      {/* Critical Actions Section */}
      <div id="critical-actions">
        <CriticalActionsSection
          tasks={data?.criticalTasks || []}
          onSendRequest={handleSendRequest}
          onSearchGmail={handleSearchGmail}
        />
      </div>

      {/* Shipments Without Bookings */}
      <ShipmentsWithoutBookingsSection
        shipments={data?.shipmentsWithoutBookings || []}
        onSendBR={handleSendBR}
        onSearchGmail={handleSearchGmail}
      />

      {/* Upcoming Shipments */}
      <UpcomingShipmentsSection
        shipments={data?.upcomingShipments || []}
        onSendSI={handleSendSI}
        onViewEmail={handleViewEmail}
      />

      {/* Document Cutoffs */}
      <div id="document-cutoffs">
        <DocumentCutoffsSection
          cutoffs={data?.documentCutoffs || []}
          onSearchGmail={handleSearchGmail}
        />
      </div>

      {/* Shipments en Route */}
      <div id="shipments-en-route">
        <ShipmentsEnRouteSection
          vessels={data?.vesselsEnRoute || []}
        />
      </div>

      {/* Arrived Without Invoice */}
      <ArrivedWithoutInvoiceSection
        shipments={data?.arrivedWithoutInvoice || []}
        onCreateInvoice={handleCreateInvoice}
      />
    </div>
  );
};

export default Dashboard;
